import React from "react";
import {
  IoIosCloudUpload,
  IoIosDocument,
  IoIosImage,
  IoIosClose,
  IoIosAlert,
} from "react-icons/io";
import { uploadAttachment } from "../api";
import "./file-upload.css";

const ACCEPT = "image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf";
const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

function isImage(url) {
  if (!url) return false;
  return /\.(png|jpe?g|webp|gif|heic|heif)(\?|$)/i.test(url);
}

function fileNameFromUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    return decodeURIComponent(u.pathname.split("/").pop() || "");
  } catch {
    return url.split("/").pop() || "";
  }
}

/**
 * FileUpload component.
 *
 * Props:
 * - value: string | null — current attachment URL
 * - onChange: (url | null) => void
 * - label: string (default "Lampiran")
 * - disabled: bool
 */
export default function FileUpload({ value, onChange, label, disabled }) {
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");
  const inputRef = React.useRef(null);

  const handleSelect = async (e) => {
    setError("");
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(`File terlalu besar — maksimum ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const url = await uploadAttachment(file);
      onChange(url);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.error &&
          err.response.data.error.message) ||
        "Gagal upload. Coba lagi.";
      setError(msg);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clear = (e) => {
    if (e) e.stopPropagation();
    onChange(null);
    setError("");
  };

  const labelText = label || "Lampiran";
  const showAsImage = isImage(value);

  return (
    <div className="file-upload">
      <div className="file-upload__label">{labelText}</div>

      {value ? (
        <div
          className={`file-upload__preview ${
            showAsImage ? "file-upload__preview--image" : ""
          }`}
        >
          {showAsImage ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="file-upload__thumb-link"
            >
              <img src={value} alt="" className="file-upload__thumb" />
            </a>
          ) : (
            <span className="file-upload__file-icon">
              <IoIosDocument />
            </span>
          )}
          <div className="file-upload__body">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="file-upload__name"
              title={fileNameFromUrl(value)}
            >
              {fileNameFromUrl(value)}
            </a>
            <div className="file-upload__hint">Klik untuk lihat di tab baru</div>
          </div>
          {!disabled && (
            <button
              type="button"
              className="file-upload__clear"
              onClick={clear}
              aria-label="Hapus lampiran"
              disabled={uploading}
            >
              <IoIosClose />
            </button>
          )}
        </div>
      ) : (
        <label
          className={`file-upload__dropzone ${
            uploading ? "file-upload__dropzone--busy" : ""
          } ${disabled ? "file-upload__dropzone--disabled" : ""}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleSelect}
            disabled={disabled || uploading}
            className="file-upload__input"
          />
          <span className="file-upload__dropzone-icon">
            {uploading ? (
              <span className="file-upload__spinner" />
            ) : (
              <IoIosCloudUpload />
            )}
          </span>
          <div className="file-upload__dropzone-body">
            <div className="file-upload__dropzone-title">
              {uploading ? "Mengupload…" : "Pilih file untuk diupload"}
            </div>
            <div className="file-upload__dropzone-sub">
              <IoIosImage /> Gambar atau <IoIosDocument /> PDF · maksimum {MAX_MB} MB
            </div>
          </div>
        </label>
      )}

      {error && (
        <div className="file-upload__error" role="alert">
          <IoIosAlert /> {error}
        </div>
      )}
    </div>
  );
}
